import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, IconButton, Tooltip, Badge, Avatar, useTheme, SvgIcon } from "@mui/material";
import { showFavorites } from "../../../store/slices/productsSlice";
import UserMenu from "../UserMenu/UserMenu";

/**
 * Componente responsivo de ações do usuário com badges dinâmicos.
 * Exibe favoritos, carrinho e perfil com contadores em tempo real.
 * Tamanhos adaptativos: mobile (24px) e desktop (30px).
 * Exibe avatar do usuário quando disponível, caso contrário mostra ícone padrão.
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {Function} props.onCartClick - Callback para abrir drawer do carrinho
 * @param {Function} [props.onUserClick] - Callback para ações do usuário (deprecated)
 * @param {boolean} [props.isMobile=false] - Se está em modo mobile
 * @param {boolean} [props.showFavorites=true] - Se deve exibir botão de favoritos
 * @returns {JSX.Element} Grupo de ícones com badges, tooltips e avatar do usuário
 */
function UserActions({ onCartClick, onUserClick, isMobile = false, showFavorites: showFavoritesIcon = true }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { items: favorites } = useSelector((state) => state.favorites);
  const { items: cart } = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  
  const [avatarHovered, setAvatarHovered] = useState(false);
  
  const cartItemsCount = cart.length;
  const iconSize = isMobile ? 30 : 36;

  const getAvatarSrc = (user) => {
    if (!user || !user.avatar) return null;

    if (typeof user.avatar === "string" && user.avatar.startsWith("data:")) {
      return user.avatar;
    }

    if (typeof user.avatar === "string" && /^[A-Za-z0-9+/=]+$/.test(user.avatar)) {
      return "data:image/png;base64," + user.avatar;
    }

    if (user.avatar && typeof user.avatar === "object" && Array.isArray(user.avatar.data)) {
      const binary = String.fromCharCode(...user.avatar.data);
      const b64 = btoa(binary);
      return "data:image/png;base64," + b64;
    }

    return null;
  };

  const handleUserClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const avatarSrc = getAvatarSrc(user);
  const hasAvatar = !!avatarSrc;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
      {showFavoritesIcon && (
        <Tooltip title="Favoritos">
          <IconButton
            color="inherit"
            size={isMobile ? "small" : "medium"}
            onClick={() => dispatch(showFavorites())}
          >
            <Badge badgeContent={favorites.length} color="secondary">
              <SvgIcon sx={{ fontSize: iconSize, color: '#B5745D' }}>
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </SvgIcon>
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      
      <Tooltip title="Carrinho de compras">
        <IconButton 
          onClick={onCartClick} 
          color="inherit" 
          size={isMobile ? "small" : "medium"}
        >
          <Badge badgeContent={cartItemsCount} color="secondary">
            <SvgIcon sx={{ fontSize: iconSize, color: '#B5745D' }}>
              <svg viewBox="0 0 16 16" fill="none">
                <path d="M14.0001 15.0002H2L3.20001 4.81819H12.8001L14.0001 15.0002Z" stroke="currentColor" strokeWidth="1.0" strokeMiterlimit="10" strokeLinecap="square" strokeLinejoin="miter" />
                <path d="M6.12476 7.12512V3.72227C6.12476 2.22502 7.05289 1 8.18728 1C9.32167 1 10.2498 2.22502 10.2498 3.72227V7.12512" stroke="currentColor" strokeWidth="1.0" strokeMiterlimit="10" strokeLinecap="square" strokeLinejoin="miter" />
              </svg>
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Perfil do usuário">
        {isMobile ? (
          <IconButton
            onClick={handleUserClick}
            color="inherit"
            size="small"
            onTouchStart={() => setAvatarHovered(true)}
            onTouchEnd={() => setAvatarHovered(false)}
          >
            {hasAvatar ? (
              <Box sx={{ position: 'relative', width: iconSize, height: iconSize }}>
                <Avatar
                  src={avatarSrc}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: avatarHovered
                      ? 'translate(-50%, -50%) scale(2.5)'
                      : 'translate(-50%, -50%) scale(1)',
                    zIndex: avatarHovered ? theme.zIndex.tooltip : 'auto',
                    transition: 'transform 0.2s ease',
                    boxShadow: avatarHovered ? theme.shadows[8] : 'none',
                  }}
                />
              </Box>
            ) : (
              <SvgIcon sx={{ fontSize: iconSize, color: '#B5745D' }}>
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.5 19.0c1.6-2.7 3.9-4.1 5.5-4.1h0c1.6 0 3.9 1.4 5.5 4.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </SvgIcon>
            )}
          </IconButton>
        ) : (
          hasAvatar ? (
            <Box
              sx={{ position: 'relative', width: iconSize, height: iconSize }}
              onMouseEnter={() => setAvatarHovered(true)}
              onMouseLeave={() => setAvatarHovered(false)}
            >
              <Avatar
                src={avatarSrc}
                onClick={handleUserClick}
                sx={{
                  width: iconSize,
                  height: iconSize,
                  cursor: 'pointer',
                  border: `2px solid ${theme.palette.primary.main}`,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: avatarHovered
                    ? 'translate(-50%, -50%) scale(2.5)'
                    : 'translate(-50%, -50%) scale(1)',
                  zIndex: avatarHovered ? theme.zIndex.tooltip : 'auto',
                  transition: 'transform 0.2s ease',
                  boxShadow: avatarHovered ? theme.shadows[8] : 'none',
                }}
              />
            </Box>
          ) : (
            <SvgIcon
              onClick={handleUserClick}
              sx={{
                cursor: "pointer",
                fontSize: iconSize,
                color: '#B5745D',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6.5 19.0c1.6-2.7 3.9-4.1 5.5-4.1h0c1.6 0 3.9 1.4 5.5 4.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </SvgIcon>
          )
        )}
      </Tooltip>
      
      <UserMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      />
    </Box>
  );
}

export default UserActions;